import { Application } from '@/types/application';
import { Detail } from '@/types/detail';
import { Configuration, Metadata } from '@/types/metadata/metadata';
import { ValidationError, ValidationObject } from '@/types/validation';
import { isArray, isConfigurationMultiple, isObject } from '@/util/typeGuards';
import { validateDetail } from './blocks';

export const multipleUtil = (): ValidationObject<Detail[] | undefined> & {
  validateBasic: (blockName: string, configuration: Configuration, details: Detail) => ValidationError[];
} => {
  const validateBasic = (blockName: string, configuration: Configuration, details: Detail): ValidationError[] => {
    const errors: ValidationError[] = [];
    const values = getValues(configuration, details);
    if (configuration.required && !values?.length) {
      errors.push({
        severity: 'error',
        message: `Block ${blockName} is missing multiple required values: ${configuration.label}`,
        id: parseInt(blockName)
      });
    }
    return errors;
  };

  const validate = (
    application: Application,
    metadata: Metadata,
    linkedSubroutineId: number,
    nodeId: number,
    blockName: string,
    configuration: Configuration,
    details: Detail
  ) => {
    const errors: ValidationError[] = [];
    if (isConfigurationMultiple(configuration)) {
      const basicErrors = validateBasic(blockName, configuration, details);
      if (basicErrors.length) return basicErrors;
      const values = getValues(configuration, details);
      const validatedValues =
        values
          ?.map((value) => {
            return validateDetail(
              application,
              metadata,
              linkedSubroutineId,
              nodeId,
              blockName,
              configuration.sections[0],
              value
            );
          })
          .flat() || [];
      if (validatedValues.length) {
        errors.push(...validatedValues);
      }
    }
    return errors;
  };

  const getValues = (configuration: Configuration, variable: unknown): Detail[] | undefined => {
    const initVal = variable as any;
    if (isArray(initVal)) {
      return initVal as Detail[];
    } else if (isObject(initVal)) {
      const prop = initVal[configuration.value];
      return getValues(configuration, prop);
    }
    return;
  };

  return {
    validateBasic: validateBasic,
    validate: validate,
    getValue: getValues
  };
};
